import BankAccountOnFile from '@liveauctioneers/caterwaul-components/lib/BankAccountOnFile/BankAccountOnFile';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderPlaidLinkToken } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import {
    getPlaidError,
    getPlaidFetching,
    getPlaidLinkToken,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';

import BankAccountOnFileWithPlaidVerify from './BankAccountOnFileWithPlaidVerify';

type Props = React.ComponentProps<typeof BankAccountOnFile>;

/**
 * This component has a container because we cannot call the usePlaid link unless we have a token, or it will fail.
 */
const BankAccountOnFileWithPlaidVerifyContainer = ({ bankAccount, ...rest }: Props) => {
    const dispatch = useAppDispatch();

    const bidderId = useAppSelector(getBidderId);
    const { plaidToken: accessToken } = bankAccount;

    const plaidLinkToken = useAppSelector((state) => getPlaidLinkToken(state, accessToken));
    const plaidFetching = useAppSelector(getPlaidFetching);
    const plaidError = useAppSelector(getPlaidError);

    useEffect(() => {
        if (!plaidError && !plaidLinkToken && !plaidFetching && bidderId > 0 && accessToken !== '') {
            dispatch(
                fetchBidderPlaidLinkToken({
                    accessToken,
                    bidderId,
                })
            );
        }
    }, [accessToken, bidderId, dispatch, plaidError, plaidFetching, plaidLinkToken]);

    if (plaidFetching) {
        return (
            <StyledNotReady>
                <Throbber testid="bankaccount-on-file-with-plaid-loading" />
            </StyledNotReady>
        );
    }

    if (plaidError) {
        return (
            <StyledNotReady>
                <FormattedMessage id="plaid.errorLoading" />
            </StyledNotReady>
        );
    }

    if (plaidLinkToken) {
        return (
            <BankAccountOnFileWithPlaidVerify
                bankAccount={bankAccount}
                {...rest}
            />
        );
    }

    return null;
};

export default BankAccountOnFileWithPlaidVerifyContainer;

const StyledNotReady = styled.div`
    display: flex;
    margin-left: 16px;
`;
